import { createContext, FC, useContext, useEffect, useState } from 'react'
import { Locale as FnsLocale, setDefaultOptions } from 'date-fns'
import i18n, { dateLocales } from 'lib/i18'
import { DropdownOption } from 'components/IconDropdown/IconDropdown'

interface LocaleContextTypes {
  selectedLocale: FnsLocale
  changeLangFormat: (
    selectedLanguage: string,
    languageOptions: DropdownOption[]
  ) => string
  handleLanguageChange: (
    selectedLanguage: string,
    languageOptions: DropdownOption[]
  ) => void
}

const LocaleContext = createContext<LocaleContextTypes | null>(null)

const LocaleProvider: FC = ({ children }) => {
  const [selectedLocale, setSelectedLocale] = useState<FnsLocale>(
    dateLocales[i18n.language as keyof typeof dateLocales]
  )

  // changes format from 'en' to 'English' and vice versa when necessary using languageOptions
  const changeLangFormat = (
    selectedLanguage: string,
    languageOptions: DropdownOption[]
  ): string => {
    const language = languageOptions.filter((lang) =>
      selectedLanguage.length === 2
        ? lang.value === selectedLanguage.trim()
        : lang.label === selectedLanguage.trim()
    )
    const langFormat =
      selectedLanguage.length === 2 ? language[0].label : language[0].value
    return langFormat
  }

  const handleLanguageChange = (
    selectedLanguage: string,
    availableLanguageOptions: DropdownOption[]
  ): void => {
    // Setting selected language in local storage
    i18n.changeLanguage(
      changeLangFormat(selectedLanguage, availableLanguageOptions)
    )
    document.documentElement.lang = i18n.language
    setSelectedLocale(dateLocales[i18n.language as keyof typeof dateLocales])
  }

  useEffect(() => {
    setDefaultOptions({ locale: selectedLocale })
  }, [i18n.language])

  return (
    <LocaleContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        selectedLocale,
        handleLanguageChange,
        changeLangFormat,
      }}>
      {children}
    </LocaleContext.Provider>
  )
}

export const useLocale = () => useContext(LocaleContext) as LocaleContextTypes

export default LocaleProvider
